import { IEventTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web'
import { Action, Reducer } from 'redux'
import { getAppInsights } from '../common/TelemetryService'
import { ApiException } from '../generated/ApiClient'
import { isProblemDetails } from '../generated/ApiClientExtensions'

type AlertType = 'success' | 'error' | 'warning' | 'info'

export interface Message {
    title: string
    message: string
    alertType: AlertType
}

export interface SnackbarMessage extends Message {
    key: number
}

export interface SnackbarState {
    snackPack: SnackbarMessage[]
}

interface SnackbarAddAction {
    type: 'snackbar/ADD_MESSAGE'
    message: SnackbarMessage
}

interface SnackbarRemoveAction {
    type: 'snackbar/POP_MESSAGE'
}

export type SnackbarAction = SnackbarAddAction | SnackbarRemoveAction

export const actionCreators = {
    addMessage: (message: Message): SnackbarAction => {
        const insights = getAppInsights()
        insights?.trackEvent({ name: message.title, properties: message })
        return { type: 'snackbar/ADD_MESSAGE', message: { ...message, key: new Date().getTime() } }
    },
    /** adds an unknown error, this could be a problem details object from the api or a JS error. 
     * @param action can be used to control how the alert will appear
     * @param title when set, will override the title or message from the error */
    addError: (error: any, action: AlertType = 'error', title?: string): SnackbarAction => {
        const insights = getAppInsights()
        if (isProblemDetails(error)) {
            insights?.trackException({ exception: new Error(error.detail), severityLevel: SeverityLevel.Error, properties: { ...error } })
            return actionCreators.addMessage({ title: title ?? error.title!, message: error.detail!, alertType: action })
        }
        else if (ApiException.isApiException(error)) {
            insights?.trackException({ exception: error, severityLevel: SeverityLevel.Error, properties: { ...error } })
            return actionCreators.addMessage({ title: title ?? error.name, message: error.message, alertType: action })
        }
        else if ('message' in error && 'stack' in error) {
            // assume it's a regular Error. https://stackoverflow.com/a/30469297/5951133
            insights?.trackException({ exception: error, severityLevel: SeverityLevel.Error, properties: { ...error } })
            return actionCreators.addMessage({ title: title ?? error.message, message: error.stack, alertType: action })
        }
        else {
            insights?.trackException({ exception: new Error((title ? `${title}: ` : '') + 'Unknown error occurred on the frontend'), severityLevel: SeverityLevel.Error, properties: { ...error } })
            // ?? anything can be thrown in javascript, stringify the error so that it doesn't crash react
            return actionCreators.addMessage({ title: title ?? 'Unknown error', message: JSON.stringify(error), alertType: action })
        }
    },
    popMessage: (): SnackbarAction => {
        return { type: 'snackbar/POP_MESSAGE' }
    },
}

const unloadedState = { snackPack: [] }

export const reducer: Reducer<SnackbarState> = (state: SnackbarState | undefined, incomingAction: Action): SnackbarState => {
    if (state === undefined)
        return unloadedState

    const action = incomingAction as SnackbarAction
    switch (action.type) {
        case 'snackbar/ADD_MESSAGE':
            return {
                snackPack: [...state.snackPack, action.message]
            }
        case 'snackbar/POP_MESSAGE':
            return {
                snackPack: state.snackPack.slice(1)
            }
    }
    return state
}